/**
 * Interactive 3D Project Card
 * 
 * A truly pioneering project card with:
 * - 3D tilt effect following mouse movement
 * - Magnetic hover interactions
 * - Parallax layers for depth
 * - Smooth animations with Framer Motion
 * - Interactive reveal of project details
 * - Dynamic lighting effects
 */

'use client';

import React, { useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';

export interface Interactive3DProjectCardProps {
  project: PropertyDevelopmentProject;
  href: string;
  brandLogo?: string;
  brandColors?: {
    primary?: string;
    accent?: string;
  };
  messages: {
    availableUnits: string;
    soldOut: string;
    comingSoon: string;
    enquireNow: string;
    viewProject: string;
    startingFrom: string;
    explore: string;
  };
  locale?: string;
  className?: string;
}

/**
 * Interactive 3D Project Card with advanced effects
 */
export function Interactive3DProjectCard({
  project,
  href,
  brandLogo,
  brandColors,
  messages,
  locale: _locale = 'en',
  className = '',
}: Interactive3DProjectCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Motion values for mouse position
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Spring animations for smooth movement
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [15, -15]), {
    stiffness: 300,
    damping: 30,
  });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-15, 15]), {
    stiffness: 300,
    damping: 30,
  });

  // Parallax layers depth
  const depth1 = useSpring(useTransform(mouseX, [-0.5, 0.5], [-10, 10]), {
    stiffness: 300,
    damping: 30,
  });
  const depth2 = useSpring(useTransform(mouseY, [-0.5, 0.5], [-20, 20]), {
    stiffness: 300,
    damping: 30,
  });

  const colors = {
    primary: brandColors?.primary || '#243462',
    accent: brandColors?.accent || '#BBA339',
  };

  const isSoldOut = project.status === 'SOLD_OUT' || project.availableUnits === 0;
  const isComingSoon = project.status === 'UPCOMING';
  const availabilityPercentage = project.totalUnits && project.soldUnits
    ? ((project.totalUnits - project.soldUnits) / project.totalUnits) * 100
    : 0;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseXPos = (e.clientX - centerX) / (rect.width / 2);
    const mouseYPos = (e.clientY - centerY) / (rect.height / 2);

    mouseX.set(mouseXPos);
    mouseY.set(mouseYPos);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <Link href={href} className={`block ${className}`}>
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
        className="relative h-[500px] rounded-3xl bg-white shadow-2xl transition-shadow duration-500 hover:shadow-[0_35px_60px_-15px_rgba(0,0,0,0.3)]"
      >
        {/* Holographic shine effect */}
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300"
          style={{
            background: `radial-gradient(
              800px circle at ${mouseX}px ${mouseY}px,
              rgba(187, 163, 57, 0.15),
              transparent 40%
            )`,
          }}
          animate={{
            opacity: isHovered ? 1 : 0,
          }}
        />

        {/* Image Container with Parallax */}
        <div className="relative h-64 overflow-hidden rounded-t-3xl">
          <motion.div
            style={{
              x: depth1,
              y: depth2,
              scale: isHovered ? 1.1 : 1,
            }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="h-full w-full"
          >
            {project.heroImageUrl ? (
              <Image
                src={project.heroImageUrl}
                alt={project.name}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
            ) : (
              <div
                className="h-full w-full bg-gradient-to-br"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
                }}
              />
            )}
          </motion.div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Floating Brand Logo */}
          {brandLogo && (
            <motion.div
              className="absolute left-4 top-4 z-10"
              style={{
                z: 50,
                x: useTransform(depth1, (v) => v * 0.5),
                y: useTransform(depth2, (v) => v * 0.5),
              }}
            >
              <div className="rounded-xl bg-white/95 p-3 shadow-2xl backdrop-blur-md">
                <Image
                  src={brandLogo}
                  alt={`${project.name} logo`}
                  width={80}
                  height={30}
                  className="h-auto w-16 object-contain"
                />
              </div>
            </motion.div>
          )}

          {/* Floating Status Badge */}
          <motion.div
            className="absolute right-4 top-4 z-10"
            style={{
              z: 50,
              x: useTransform(depth1, (v) => -v * 0.5),
              y: useTransform(depth2, (v) => v * 0.5),
            }}
          >
            {isSoldOut ? (
              <span className="inline-flex items-center rounded-full bg-red-600 px-4 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-md">
                {messages.soldOut}
              </span>
            ) : isComingSoon ? (
              <span
                className="inline-flex items-center rounded-full px-4 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-md"
                style={{ backgroundColor: colors.accent }}
              >
                {messages.comingSoon}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-green-600 px-4 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-md">
                {project.availableUnits} {messages.availableUnits}
              </span>
            )}
          </motion.div>

          {/* Project Type Badge - Bottom Floating */}
          {project.projectType && (
            <motion.div
              className="absolute bottom-4 left-4 z-10"
              style={{
                z: 50,
                x: useTransform(depth1, (v) => v * 0.3),
                y: useTransform(depth2, (v) => -v * 0.3),
              }}
            >
              <span className="inline-flex items-center rounded-lg bg-white/90 px-3 py-1.5 text-xs font-bold uppercase tracking-wider backdrop-blur-md"
                style={{ color: colors.primary }}
              >
                {project.projectType.replace(/_/g, ' ')}
              </span>
            </motion.div>
          )}
        </div>

        {/* Content Section with Elevated Layer */}
        <motion.div
          className="relative p-6"
          style={{
            z: 20,
            x: useTransform(depth1, (v) => v * 0.2),
            y: useTransform(depth2, (v) => v * 0.2),
          }}
        >
          {/* Project Name */}
          <motion.h3
            className="mb-3 text-2xl font-bold leading-tight"
            style={{ color: colors.primary }}
            animate={{
              y: isHovered ? -2 : 0,
            }}
          >
            {project.name}
          </motion.h3>

          {/* Description */}
          <motion.p
            className="mb-4 line-clamp-2 text-sm text-gray-600"
            animate={{
              opacity: isHovered ? 0.8 : 0.6,
            }}
          >
            {project.description}
          </motion.p>

          {/* Price with Animated Underline */}
          {project.displayPriceText && (
            <motion.div className="mb-4 relative inline-block">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {messages.startingFrom}
              </div>
              <div
                className="text-2xl font-bold"
                style={{ color: colors.accent }}
              >
                {project.displayPriceText}
              </div>
              <motion.div
                className="absolute -bottom-1 left-0 h-0.5 bg-current"
                style={{ backgroundColor: colors.accent }}
                animate={{
                  width: isHovered ? '100%' : '0%',
                }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          )}

          {/* Animated Progress Bar */}
          {!isSoldOut && project.totalUnits && (
            <div className="mb-4">
              <div className="mb-2 flex justify-between text-xs font-medium text-gray-500">
                <span>{project.availableUnits} Available</span>
                <span>{project.soldUnits} Sold</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: colors.accent }}
                  initial={{ width: 0 }}
                  animate={{ width: `${availabilityPercentage}%` }}
                  transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
                />
              </div>
            </div>
          )}

          {/* Interactive CTA Button */}
          <motion.div
            className="flex items-center justify-between border-t border-gray-200 pt-4"
            animate={{
              y: isHovered ? -5 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            <span className="text-xs font-medium text-gray-500">
              {project.stage?.replace(/_/g, ' ')}
            </span>

            <motion.div
              className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: colors.primary }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {messages.explore}
              <motion.svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{
                  x: isHovered ? 5 : 0,
                }}
                transition={{ duration: 0.3 }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </motion.svg>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Animated Border Glow */}
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-3xl"
          style={{
            border: `2px solid ${colors.accent}`,
            opacity: 0,
          }}
          animate={{
            opacity: isHovered ? 0.3 : 0,
          }}
          transition={{ duration: 0.3 }}
        />
      </motion.div>
    </Link>
  );
}

